import React, { FC, useEffect, useState, useMemo, useRef } from 'react';

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import SettingsIcon from '@material-ui/icons/Settings';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { Loader } from '../../../../components/Loader';
import { IconButton } from '../../../../components/UI/IconButton';
import { PaginationOption } from '../../../../types';
import useLocalStorage from '../../../../utils/hooks/useLocalStorage';
import { ANALYTICS_ROWS_PER_PAGE, sortKeyType, TableHeadingDataType, CellType } from '../../const';
import { dataToCsvString, downloadCsv, sortData } from '../../helpers';
import ArrayCell from './components/ArrayCell';
import DateCell from './components/DateCell';
import ExpandCell from './components/ExpandCell';
import ImageCell from './components/ImageCell';
import ImageTitleCell from './components/ImageTitleCell';
import LabelWithHelperTextCell from './components/LabelWithHelperTextCell';
import LinkCell from './components/LinkCell';
import ProgressCell from './components/ProgressCell';
import SecondsCell from './components/SecondsCell';
import { TableRowComponent } from './components/TableRowComponent';
import ViewDetailsCell from './components/ViewDetailsCell';
import useStyles from './styles';

export const TABLE_CELL_COMPONENTS = {
  [CellType.Link]: (styles, row, k, align) => (
    <LinkCell styles={styles} row={row} k={k} align={align} />
  ),
  [CellType.Image]: (styles, row, k, align) => (
    <ImageCell styles={styles} row={row} k={k} align={align} />
  ),
  [CellType.Seconds]: (styles, row, k, align) => (
    <SecondsCell styles={styles} row={row} k={k} align={align} />
  ),
  [CellType.Date]: (styles, row, k, align) => (
    <DateCell styles={styles} row={row} k={k} align={align} />
  ),
  [CellType.Array]: (styles, row, k, align) => (
    <ArrayCell styles={styles} row={row} k={k} align={align} />
  ),
  [CellType.Expand]: (styles, row, k, align) => (
    <ExpandCell styles={styles} row={row} k={k} align={align} />
  ),
  [CellType.ImageAndTitle]: (styles, row, k, align) => (
    <ImageTitleCell styles={styles} row={row} k={k} align={align} />
  ),
  [CellType.ViewDetails]: (styles, row, k, align) => (
    <ViewDetailsCell styles={styles} row={row} k={k} align={align} />
  ),
  [CellType.Progress]: (styles, row, k) => <ProgressCell styles={styles} row={row} k={k} />,
  [CellType.LabelWithHelperText]: (styles, row, k, align) => (
    <LabelWithHelperTextCell styles={styles} row={row} k={k} align={align} />
  ),
};

interface AnalyticsTableProps {
  headingData: TableHeadingDataType[];
  exportHeadingData?: TableHeadingDataType[];
  bodyData: any[];
  tab: number;
  label: string;
  tenantTitle?: string;
  isAdvancedView?: boolean;
  handleChangeView?: () => void;
  name?: string;
  fetchData?: (data: PaginationOption) => void;
  dataCount?: number;
  handleExportCsv?: () => void;
  isLoading?: boolean;
  isExportDisabled?: boolean;
  handleSortChanged?: (sortKey: sortKeyType) => void;
  sortKey?: sortKeyType;
}

const AnalyticsTable: FC<AnalyticsTableProps> = ({
  headingData,
  exportHeadingData,
  bodyData,
  tab,
  label,
  tenantTitle,
  isAdvancedView = false,
  handleChangeView,
  fetchData,
  dataCount,
  handleExportCsv,
  isLoading,
  isExportDisabled = false,
  name,
  sortKey,
  handleSortChanged,
}) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const [sortedData, setSortedData] = useState([]);
  const [page, setPage] = React.useState(0);
  const { value: rowsPerPage, updateLocStorageValue } = useLocalStorage(
    ANALYTICS_ROWS_PER_PAGE,
    10,
  );
  const fetchDataRef = useRef(fetchData);

  const hasAdvancedView = useMemo(() => !handleChangeView, [handleChangeView]);

  const handleSortClick = (key) => {
    let newSortValue: sortKeyType = null;
    if (!sortKey || sortKey?.key !== key) {
      newSortValue = { key, value: 1 };
    } else if (sortKey.key === key && sortKey.value === 1) {
      newSortValue = { key, value: 2 };
    }

    handleSortChanged && handleSortChanged(newSortValue);
  };

  useEffect(() => {
    if (sortKey?.key) {
      const originDataCopy = [...bodyData];
      const sortColumn = headingData.find((item) => item?.key === sortKey?.key);
      const sortColumnType = sortColumn?.type;
      const sortingKey = sortColumn?.sortingKey;

      setSortedData(
        sortData(originDataCopy, sortKey?.key, sortingKey, sortColumnType, sortKey?.value === 2),
      );
    }
  }, [bodyData, sortKey, headingData]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateLocStorageValue(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownloadCsv = (): void => {
    const csvData = dataToCsvString(exportHeadingData || headingData, resData, tab);
    const date = Math.floor(Date.now() / 1000);
    const fileName = tenantTitle ? `${tenantTitle}-${label}-${date}` : `${label}-${date}`;
    downloadCsv(csvData, fileName);
  };

  useEffect(() => {
    if (fetchData) {
      if (fetchData !== fetchDataRef.current && page !== 0) {
        setPage(0);
      } else {
        fetchData({
          page: page + 1,
          count_items_on_page: rowsPerPage,
        });
      }
      fetchDataRef.current = fetchData;
    }
  }, [page, rowsPerPage, fetchData]);

  const resData = sortKey ? sortedData : bodyData;

  return isLoading ? (
    <div data-testid={name + 'Loader'} className={styles.loaderWrapper}>
      <Loader global={false} />
    </div>
  ) : (
    <div className={styles.container}>
      <div className={styles.buttonsWrapper}>
        <IconButton
          disabled={hasAdvancedView}
          label={t(isAdvancedView ? 'button.basicView' : 'button.advancedView')}
          icon={<SettingsIcon fontSize="small" />}
          onClick={handleChangeView}
          data-testid="advancedViewButton"
        />
        {resData && resData.length !== 0 && (
          <IconButton
            data-testid="downloadCsv"
            disabled={isExportDisabled}
            label={t('button.downloadCsv')}
            icon={<CloudDownloadIcon fontSize="small" />}
            onClick={() => (handleExportCsv ? handleExportCsv() : handleDownloadCsv())}
          />
        )}
      </div>
      <div className={styles.tableWrap}>
        {!resData || resData.length === 0 ? (
          <div className={styles.emptyStateWrap}>
            <Typography
              variant="h2"
              component="h2"
              align="center"
              className={styles.emptyStateTitle}
            >
              {t('page.analytics.emptyData')}
            </Typography>

            <p className={styles.emptyStateSubTitle}>{t('page.analytics.noDataRecorder')}</p>
          </div>
        ) : (
          <>
            <Table className={styles.tableInnerWrap}>
              <TableHead>
                <TableRow className={styles.tableHead}>
                  {headingData.map((item) => {
                    const isSorted = item?.key === sortKey?.key;
                    const isDesc = sortKey?.value === 1;
                    const sortable = typeof item?.sortable === 'undefined';
                    const minWidth = typeof item.minWidth === 'undefined' ? 0 : item.minWidth;
                    const align = item?.align || 'left';

                    const onClick = () => {
                      if (!sortable) return;
                      handleSortClick(item?.key);
                    };

                    return (
                      <TableCell
                        key={item.key}
                        className={cx(styles.tableHeadCell, sortable && styles.tableHeadSortable)}
                        style={{
                          minWidth,
                        }}
                        align={align}
                        onClick={onClick}
                        data-testid={item.key + 'TableSort'}
                      >
                        <Box
                          className={styles.boxHeading}
                          style={{
                            justifyContent:
                              align === 'left'
                                ? 'flex-start'
                                : align === 'center'
                                ? 'center'
                                : 'flex-end',
                          }}
                        >
                          <div>{item?.title}</div>

                          {sortable && (
                            <ArrowDropDownIcon
                              className={cx(
                                isSorted && styles.sorted,
                                isSorted && isDesc && styles.sortedDesc,
                              )}
                              fontSize="small"
                            />
                          )}
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>

              <TableBody className={styles.tableBody}>
                {resData &&
                  (fetchData
                    ? resData
                    : resData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  ).map((row) => (
                    <TableRowComponent
                      key={row?.id}
                      row={row}
                      headingData={headingData}
                      handleClickRow={row?.viewDetails}
                      name={name}
                    />
                  ))}
              </TableBody>
            </Table>

            <div className={styles.paginationWrap}>
              <TablePagination
                rowsPerPageOptions={[10, 20, 50, 100]}
                component="div"
                count={fetchData ? dataCount || 0 : resData?.length || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
                labelRowsPerPage={t('block.analyticsTable.paginationLabel')}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AnalyticsTable;
